import './forms.css';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import Compressor from 'compressorjs';
import Veil from '../Veil';
import Loader from '../Loader';

import { isHeic, planDrop, nextQueueId } from './imageQueue';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  EllipsisVerticalIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {
  ExclamationTriangleIcon,
  TrashIcon,
  CameraIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/solid';

function SortableItem(props) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="relative cursor-default">
        <div
          className="btn btn-sm btn-square cursor-pointer absolute top-2 left-2 -space-x-5 flex flex-row z-10 touch-none"
          {...listeners}
        >
          <EllipsisVerticalIcon className="h-4 w-4" />
          <EllipsisVerticalIcon className="h-4 w-4" />
        </div>
        {props.children}
      </div>
    </div>
  );
}

const ImageUpload = ({
  input,
  label,
  meta,
  onChange,
  maxImages = 7,
  handleDeleteImage,
  desiredWidth = 2000,
  desiredHeight = 2000,
  labelStyle = '',
  previewSize = 'md',
  circular = false,
  submitting,
  desiredAspectRatio = null,
  verticalRatio = 4 / 5,
  horizontalRatio = 7 / 5,
  takeImageAspect = false,
  required = true, // if image is required, don't allow deletion of last image
  quality = 0.85,
}) => {
  const croppedThumbsClassName =
    'cropped_thumbnails relative w-36 h-36 aspect-square justify-center';

  const widthClassName =
    previewSize === 'md' ? 'w-36' : previewSize === 'sm' ? 'w-24' : 'w-36';

  const [files, setFiles] = useState([]);
  // files pending preparation (HEIC transcode / downscale), processed one at a time
  const [pendingQueue, setPendingQueue] = useState([]);
  // ref, not state: re-renders must never cancel or restart an in-flight prep
  const preparingRef = useRef(false);
  // user-facing messages for skipped/rejected files (#432 — never reject silently)
  const [notices, setNotices] = useState([]);

  const [croppedArea, setCroppedArea] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(desiredAspectRatio);
  const [aspectOption, setAspectOption] = useState(4 / 3);

  // input.onChange must always receive the freshest array even from async
  // callbacks — the old per-callback closures over `files` are what lost
  // images when several were dropped at once (#71, #360).
  // `input` comes from React Final Form as a NEW object every render, so it
  // lives in a ref: commitFiles must keep a stable identity or every effect
  // depending on it re-fires (and cancels work) on each render.
  const filesRef = useRef(files);
  const inputRef = useRef(input);
  inputRef.current = input;
  const commitFiles = useCallback((updater) => {
    const next =
      typeof updater === 'function' ? updater(filesRef.current) : updater;
    filesRef.current = next;
    setFiles(next);
    inputRef.current.onChange(next);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // hydrate from the form value (existing server images)
  useEffect(() => {
    if (input.value) {
      filesRef.current = input.value;
      setFiles(input.value);
    }
  }, [input.value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': [],
      'image/heic': ['.heic'],
      'image/heif': ['.heif'],
    },
    // no dropzone maxFiles: it rejects the whole batch silently (#432).
    // We do our own slot accounting in planDrop and explain what was skipped.
    onDrop: (acceptedFiles, fileRejections) => {
      const { toProcess, notices: dropNotices } = planDrop({
        currentCount: filesRef.current?.length || 0,
        maxImages,
        accepted: acceptedFiles,
        rejections: fileRejections,
      });
      if (dropNotices.length) setNotices((prev) => [...prev, ...dropNotices]);
      if (toProcess.length) {
        setPendingQueue((prev) => [
          ...prev,
          ...toProcess.map((file) => ({ qid: nextQueueId(), file })),
        ]);
        setAspect(desiredAspectRatio || 1);
      }
    },
  });

  const deleteFile = (file) => {
    commitFiles((prev) => prev.filter((f) => f.imageFile !== file.imageFile));
    handleDeleteImage(file.imageFile);
  };

  const removeFile = (file) => {
    commitFiles((prev) => prev.filter((f) => f.name !== file.name));
  };

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      commitFiles((prev) => {
        const oldIndex = prev.findIndex((f) => f.imageFile === active.id);
        const newIndex = prev.findIndex((f) => f.imageFile === over.id);
        return arrayMove(prev, oldIndex, newIndex).map((f, i) => {
          if (f instanceof Blob) {
            const newBlob = new Blob([f], { type: f.type, ...f });
            for (const key in f) {
              if (f.hasOwnProperty(key)) {
                newBlob[key] = f[key];
              }
            }
            newBlob.imageOrder = i;
            return newBlob;
          }
          return { ...f, imageOrder: i };
        });
      });
    }
  }

  const renderError = ({ error, touched }) => {
    if (touched && error) {
      return (
        <div className="flex">
          <div className="text-red-500 px-2">
            <ExclamationTriangleIcon className="h-8 w-8 mt-3" />
          </div>
          <div className="text-white bg-red-400 w-fit flex-nowrap py-2 px-4 mt-2 rounded whitespace-nowrap">
            {error}
          </div>
        </div>
      );
    }
  };

  const onCropComplete = (croppedAreaPercentage, croppedAreaPixels) => {
    setCroppedArea(croppedAreaPixels);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => {
        resolve(image);
      });
      image.addEventListener('error', reject);
      image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc,
    pixelCrop,
    rotation = 0,
    imageName
  ) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea - 2;
    canvas.height = safeArea - 2;

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5 - 1,
      safeArea / 2 - image.height * 0.5 - 1
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width - 1;
    canvas.height = pixelCrop.height - 1;

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
      0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y
    );

    const file = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          new Compressor(blob, {
            width: desiredWidth,
            height: desiredHeight,
            quality,
            success(result) {
              resolve(
                Object.assign(result, {
                  imageFile: 'cropped_' + imageName,
                  name: 'cropped_' + imageName,
                  preview: URL.createObjectURL(result),
                  croppedImage: true,
                  imageOrder: 0,
                })
              );
            },
            error(err) {
              reject(err);
            },
          });
        },
        'image/jpeg',
        quality
      );
    });

    return file;
  };

  // prepare ONE queued file at a time: HEIC -> JPEG (#297), oversize -> downscale,
  // then hand to the cropper. Sequential processing kills the parallel-callback
  // races that hung multi-file drops (#71) and threw null-width errors (#360).
  useEffect(() => {
    if (preparingRef.current || pendingQueue.length === 0) return;
    const entry = pendingQueue[0];
    preparingRef.current = true;

    (async () => {
      try {
        let blob = entry.file;

        if (isHeic(blob)) {
          const heic2any = (await import('heic2any')).default;
          const converted = await heic2any({
            blob,
            toType: 'image/jpeg',
            quality: 0.92,
          });
          blob = Array.isArray(converted) ? converted[0] : converted;
        }

        // read dimensions
        const objectUrl = URL.createObjectURL(blob);
        const image = await createImage(objectUrl);
        const { width, height } = image;

        const imageName =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);

        let prepared;
        if (width > desiredWidth || height > desiredHeight) {
          // downscale proportionally before loading into the cropper
          let newWidth = width;
          let newHeight = height;
          if (width > height) {
            newHeight *= desiredWidth / width;
            newWidth = desiredWidth;
          } else {
            newWidth *= desiredHeight / height;
            newHeight = desiredHeight;
          }
          const resized = await new Promise((resolve, reject) => {
            new Compressor(blob, {
              width: newWidth,
              height: newHeight,
              quality,
              success: resolve,
              error: reject,
            });
          });
          URL.revokeObjectURL(objectUrl);
          prepared = {
            ...resized,
            cropper: URL.createObjectURL(resized),
            croppedImage: false,
            name: imageName,
          };
        } else {
          prepared = {
            ...blob,
            cropper: objectUrl,
            croppedImage: false,
            name: imageName,
          };
        }

        window.scrollTo(0, 0);
        commitFiles((prev) => [...prev, prepared]);
      } catch (err) {
        setNotices((prev) => [
          ...prev,
          `"${entry.file?.name || 'A file'}" couldn't be read as an image.`,
        ]);
      } finally {
        // ALWAYS drain the queue and release the lock — an earlier version
        // skipped this when a re-render raced the prep, wedging the queue
        preparingRef.current = false;
        setPendingQueue((prev) => prev.filter((e) => e.qid !== entry.qid));
      }
    })();
  }, [pendingQueue, commitFiles, desiredWidth, desiredHeight, quality]);

  // sort the files by imageOrder ascending from 0
  const sortedFiles =
    files &&
    files.sort((a, b) => {
      if (a.imageOrder && b.imageOrder) {
        return a.imageOrder - b.imageOrder;
      } else if (a.imageOrder) {
        return 1;
      } else if (b.imageOrder) {
        return -1;
      } else {
        return 0;
      }
    });

  // exactly ONE file in the cropper at a time; the rest wait their turn.
  // (Previously every un-cropped file mounted its own full-screen cropper
  // sharing one crop/zoom state.)
  const cropEntry =
    pendingQueue.length === 0
      ? (sortedFiles || []).find((f) => f.cropper && !f.croppedImage)
      : null;
  const cropsRemaining = (sortedFiles || []).filter(
    (f) => f.cropper && !f.croppedImage
  ).length;

  // fresh crop state for each image entering the cropper
  useEffect(() => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedArea(null);
  }, [cropEntry?.name]);

  const showDeleteButton = (required && files.length > 0) || !required;

  const thumbs =
    sortedFiles &&
    sortedFiles.map((file, index) => {
      if (file.croppedImage) {
        return (
          <SortableItem key={file.name} id={file.name}>
            <div key={file.name} className={croppedThumbsClassName}>
              {showDeleteButton && (
                <button
                  className="btn absolute top-2 right-2 btn-sm"
                  onClick={() => removeFile(file)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              {!submitting ? (
                <img
                  src={file.preview}
                  alt="new files"
                  className={
                    circular
                      ? 'rounded-full object-contain object-center'
                      : 'rounded-2xl object-contain object-center bg-gray-200 dark:bg-gray-900 animate-fade-in'
                  }
                />
              ) : (
                <Loader type="inline" />
              )}
            </div>
          </SortableItem>
        );
      } else if (file._id || (file.imageFile && !file.croppedImage)) {
        // show existing images on the build stored in AWS
        const { REACT_APP_AWS_S3 } = process.env;
        const imageUri = REACT_APP_AWS_S3 + '/' + file.imageFile;

        return (
          <SortableItem key={file.imageFile} id={file.imageFile}>
            <div key={imageUri} className={croppedThumbsClassName}>
              {showDeleteButton && (
                <button
                  className="btn absolute top-2 right-2 btn-sm"
                  onClick={() => deleteFile(file)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              {!submitting ? (
                <img
                  src={imageUri}
                  alt="existing file"
                  className={
                    circular
                      ? 'rounded-full object-contain object-center '
                      : 'rounded-2xl object-contain object-center bg-gray-200 dark:bg-gray-900 animate-fade-in'
                  }
                />
              ) : (
                <Loader type="inline" />
              )}
            </div>
          </SortableItem>
        );
      } else if (file.cropper && !file.croppedImage) {
        // placeholder thumb while this file waits for (or is in) the cropper
        return (
          <div key={file.name} className={croppedThumbsClassName}>
            <Loader type="inline" />
          </div>
        );
      }
      return <Loader key={index} active type="inline" />;
    });

  return (
    <div>
      {renderError(meta)}
      {notices.length > 0 && (
        <div className="alert alert-warning my-2 flex flex-row justify-between items-start">
          <div className="flex flex-col gap-1 text-sm">
            {notices.map((notice, i) => (
              <span key={i}>{notice}</span>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() => setNotices([])}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      <label className={labelStyle}>{label}</label>
      <div
        className={`grid grid-flow-col auto-cols-max items-center overflow-x-auto gap-4 w-full ${labelStyle}`}
      >
        {maxImages >= (files && files.length + 1) && (
          <div
            {...getRootProps({
              className: `dropzone p-5 ${widthClassName} h-[98%] flex  ${
                circular ? 'rounded-full' : 'rounded-2xl'
              } ${
                files?.length > 0
                  ? `scale-100 !w-24 !h-24`
                  : `scale-100 bg-gray-200 dark:bg-gray-800 aspect-square `
              } border border-gray-400 dark:border-gray-600 border-dashed border-solid items-center justify-center hover:cursor-pointer hover:bg-gray-400 dark:hover:bg-gray-700 transition-all duration-300 ease-in-out`,
            })}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the file here</p>
            ) : (
              <div
                className={`fixed w-full justify-center items-center text-center flex flex-col`}
              >
                {files?.length > 0 && (
                  <>
                    <PlusCircleIcon className="h-8 w-8 text-center" />
                  </>
                )}
                <div
                  className={`fixed w-full justify-center items-center text-center flex flex-col transition-transform duration-300  ${
                    files?.length > 0 ? `opacity-0` : `animate-fade-in `
                  }`}
                >
                  <CameraIcon className="h-7 w-7 text-center" />
                  {!circular && <span>Tap to select</span>}
                </div>
              </div>
            )}
          </div>
        )}
        <aside className="flex flex-wrap flex-row gap-4 items-center justify-center">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedFiles.map((item) => item.imageFile)}
              strategy={horizontalListSortingStrategy}
            >
              {thumbs}
            </SortableContext>
          </DndContext>
        </aside>
      </div>
      {cropEntry && (
        <Veil key={cropEntry.name}>
          <div className="z-20">
            <Cropper
              image={cropEntry.cropper}
              crop={crop}
              zoom={zoom}
              aspect={aspect || aspectOption}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onMediaLoaded={(mediaSize) => {
                if (mediaSize.height >= mediaSize.width) {
                  setAspectOption(verticalRatio);
                } else {
                  setAspectOption(horizontalRatio);
                }
              }}
              className="cropperContainer"
            />
            <div className="reactEasyCrop_Controls">
              <div className="flex flex-row mb-14 gap-4 items-center">
                {cropsRemaining > 1 && (
                  <span className="badge badge-neutral shadow-lg">
                    {cropsRemaining - 1} more to crop
                  </span>
                )}
                <button
                  className="btn btn-warning shadow-lg"
                  disabled={submitting || !croppedArea}
                  onClick={async (event) => {
                    event.target.disabled = true;
                    event.target.innerText = 'saving...';
                    event.preventDefault();

                    const croppedImage = await getCroppedImg(
                      cropEntry.cropper,
                      croppedArea,
                      0,
                      cropEntry.name
                    );

                    commitFiles((prev) => [
                      ...prev.filter((f) => f.name !== cropEntry.name),
                      croppedImage,
                    ]);
                  }}
                >
                  Save
                </button>
                {!takeImageAspect && !desiredAspectRatio && (
                  <button
                    className="btn btn-accent shadow-lg"
                    onClick={() => {
                      if (aspect === 1) {
                        setAspect(aspectOption);
                      } else if (aspect !== 1) {
                        setAspect(1);
                      }
                    }}
                  >
                    {aspect === 1 ? (
                      <ArrowsPointingOutIcon className="h-6 w-6" />
                    ) : (
                      <ArrowsPointingInIcon className="h-6 w-6" />
                    )}
                  </button>
                )}
                {takeImageAspect && (
                  <button
                    className="btn btn-accent shadow-lg"
                    onClick={() => {
                      if (aspect === 1) {
                        const image = new Image();
                        image.src = cropEntry.cropper;
                        image.onload = () => {
                          setAspect(image.width / image.height);
                        };
                      } else if (aspect !== 1) {
                        setAspect(1);
                      }
                    }}
                  >
                    {aspect === 1 ? (
                      <ArrowsPointingOutIcon className="h-6 w-6" />
                    ) : (
                      <ArrowsPointingInIcon className="h-6 w-6" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Veil>
      )}
    </div>
  );
};

export default ImageUpload;
